import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PshipCodeMastService } from '../../services/pship-code-mast.service';
import * as _ from 'lodash-es';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
  generalConfigFromGlobal,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-pship-code-mast',
    templateUrl: './pship-code-mast.component.html',
    styleUrls: ['./pship-code-mast.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class PshipCodeMastComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();
  private readonly subs = new Subscription();

  // 🔴 STANDARD COMPONENT PROPERTIES (SuperPrompt V7)
  currentTabName = "Form Entry";
  minimise = false;
  isSaveClicked = false;
  
  // Input properties from AngularJS
  baseURLs: any = {};
  @Input('capture-custom-events') captureCustomEvents: any;

  // 🔴 STANDARD MODAL PROPERTIES
  modalTitle = "Edit Partnership Code";
  loading = false;
  showFormLoader = false;
  crudLoading = false;
  dataLoaded = false; // Flag to prevent multiple API calls
  
  // Form data properties from AngularJS
  code_key: string = '';
  code_desc: string = '';
  code_short_desc: string = '';
  code_group: string = '';
  from_year: number | null = null;
  to_year: number | null = null;
  priority_order: number | null = null;
  created_by: string = '';
  created_on: string = '';
  updated_by: string = '';
  updated_on: string = '';
  is_display: string = 'Y';
  code_disp_desc: string = '';
  is_default: string = 'N';
  code_long_desc: string = '';
  row_type: string = '';
  
  vaildationErrMsg: string | null = null;
  logged_in_user: string = '';
  
  // Services and configurations
  globalService: any;
  filterParams: any = {};
  GENERAL_CONFIG: any;
  USER_SETTINGS: any;
  rowData: any = {};

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private pshipCodeMastService: PshipCodeMastService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.dataLoaded = false;
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.dataLoaded = false;
      this.init();
    }));
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = header;
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.USER_SETTINGS = header.userSettingsData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen) || {};
  }

  init() {
    try {
      if (this.rowData && Object.keys(this.rowData).length > 0 && 
          this.GENERAL_CONFIG && this.USER_SETTINGS && !this.dataLoaded) {
        
        this.dataLoaded = true;
        this.logged_in_user = this.USER_SETTINGS.user.sso_id;
        
        // Load data from rowData
        this.code_key = this.rowData.CODE_KEY || '';
        this.code_desc = this.rowData.CODE_DESC || '';
        this.code_short_desc = this.rowData.CODE_SHORT_DESC || '';
        this.code_group = this.rowData.CODE_GROUP || '';
        this.from_year = this.rowData.FROM_YEAR || null;
        this.to_year = this.rowData.TO_YEAR || null;
        this.priority_order = this.rowData.PRIORITY_ORDER || null;
        this.created_by = this.rowData.CREATED_BY || '';
        this.created_on = this.rowData.CREATED_ON || '';
        this.updated_by = this.rowData.UPDATED_BY || '';
        this.updated_on = this.rowData.UPDATED_ON || '';
        this.is_display = this.rowData.IS_DISPLAY || 'Y';
        this.code_disp_desc = this.rowData.CODE_DISP_DESC || '';
        this.is_default = this.rowData.IS_DEFAULT || 'N';
        this.code_long_desc = this.rowData.CODE_LONG_DESC || '';
        this.row_type = this.rowData.ROW_TYPE || '';
        
        this.cdr.detectChanges();
      }
    } catch (e) {
      console.error('Error in PshipCodeMast init:', e);
    }
  }

  validateFormData(): boolean {
    let validationStatus = true;
    this.vaildationErrMsg = null;

    if (this.from_year && this.to_year && this.from_year > this.to_year) {
      this.vaildationErrMsg = 'From Year cannot be greater than To Year.';
      validationStatus = false;
    }

    return validationStatus;
  }

  save() {
    this.isSaveClicked = true;
    this.processSave();
  }

  processSave() {
    const validationStat = this.validateFormData();

    if (validationStat) {
      console.log('Validation passed... sending details to service layer for saving in the DB');
      
      this.crudLoading = true;
      
      const clobSettingsObj: any = {
        sso_id: this.logged_in_user
      };

      const clobDataObj: any = {
        code_key: this.code_key,
        code_desc: this.code_desc,
        code_short_desc: this.code_short_desc,
        from_year: this.from_year,
        to_year: this.to_year,
        priority_order: this.priority_order,
        updated_by: this.logged_in_user,
        is_display: this.is_display,
        code_disp_desc: this.code_disp_desc,
        is_default: this.is_default,
        code_long_desc: this.code_long_desc,
        row_type: this.row_type
      };

      const message = "Partnership Code Master Details Saved Successfully";
      
      this.sendDetails(clobSettingsObj, clobDataObj, message);
    } else {
      this._snackBar.open(this.vaildationErrMsg || 'Validation failed', 'Close', {
        duration: 4000,
        panelClass: ['error-snackbar']
      });
      this.isSaveClicked = false;
    }
  }

  sendDetails(clobSettings: any, clobData: any, msg: string) {
    this.pshipCodeMastService.saveACodeMastRecord(
      this.GENERAL_CONFIG.base_url,
      clobSettings,
      clobData,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;

        if (response.errorMessage && response.errorMessage !== 'null') {
          this._snackBar.open(response.errorMessage, 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else {
          this._snackBar.open(msg, 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          
          // 🔴 CRITICAL: Emit 'refresh' string for AngularJS modal close
          this.requestGridUpdate.emit('refresh');
        }
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this._snackBar.open('Error occurred while saving. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        console.error('Error saving code master:', error);
      }
    );
  }

  cancel() {
    // 🔴 CRITICAL: Use EventEmitter for AngularJS modal dismissal
    this.requestGridUpdate.emit('cancel');
  }

  // Radio button change handlers
  onIsDisplayChange(value: string) {
    this.is_display = value;
  }

  onIsDefaultChange(value: string) {
    this.is_default = value;
  }
}

